import {
  CourseSubject,
  ExamBoards,
  ExamLevel,
  ExamResourcesDifficulty,
  ICreateExamResource,
  QuestionFormat,
  ResourceType,
} from '../types'
import { matchToClosestCorrectValue } from './string'

export const examResourceDifficultyOptionsSwitchObj: Record<
  ExamResourcesDifficulty,
  {
    name: ExamResourcesDifficulty
    title: string
    bgColor: string
    textColor: string
    borderColor: string
  }
> = {
  [ExamResourcesDifficulty.Hard]: {
    name: ExamResourcesDifficulty.Hard,
    title: 'Hard',
    bgColor: 'bg-cred-700',
    textColor: 'text-cred-700',
    borderColor: 'border-cred-700',
  },
  [ExamResourcesDifficulty.Medium]: {
    name: ExamResourcesDifficulty.Medium,
    title: 'Medium',
    bgColor: 'bg-corange-450',
    textColor: 'text-corange-450',
    borderColor: 'border-corange-450',
  },
  [ExamResourcesDifficulty.Easy]: {
    name: ExamResourcesDifficulty.Easy,
    title: 'Easy',
    bgColor: 'bg-cgreen-700',
    textColor: 'text-cgreen-700',
    borderColor: 'border-cgreen-700',
  },
}

export const erUploadLevelsAllowed: ExamLevel[] = Object.values(ExamLevel)
export const erUploadBoardsAllowed: ExamBoards[] = Object.values(ExamBoards)
export const erUploadSubjectsAllowed: CourseSubject[] =
  Object.values(CourseSubject)
export const erUploadDifficultiesAllowed: ExamResourcesDifficulty[] =
  Object.values(ExamResourcesDifficulty)
export const erUploadResourceTypesAllowed: ResourceType[] =
  Object.values(ResourceType)
export const erUploadQuestionFormatsAllowed: QuestionFormat[] =
  Object.values(QuestionFormat)

// transform string to id, "Exam Question label is" will result: "exam-question-label-is"
export const generateExamResourceId = (string: string): string => {
  return string
    .toLowerCase() // convert to lowercase
    .replace(/[^a-z0-9]+/g, '-') // replace non-alphanumeric characters with hyphens
    .replace(/(^\-|\-$)/g, '') // remove leading and trailing hyphens
}

export function checkExamResourceStrictEnumValues<T>(
  name: string,
  value: T,
  allowedValues: T[],
  setErrorMessage?: (message: string) => void,
) {
  // handle empty field
  if (!value) {
    const err = `${name} field is missing from spreadsheet`
    console.error(err)
    setErrorMessage && setErrorMessage(err)

    throw new Error(err)
  }

  // try to fix value
  const fixedValue = matchToClosestCorrectValue(
    value as string,
    allowedValues as string[],
  ) as T

  // handle bad value
  if (!allowedValues.includes(fixedValue as T)) {
    const message = `${name} field from spreadsheet is not matching predefined ${name} values. This field is case sensitive, please use one of values allowed: `
    console.error(message, JSON.stringify(allowedValues), {
      initialValue: value,
      fixedValue,
    })

    setErrorMessage &&
      setErrorMessage(message + `${JSON.stringify(allowedValues, null, 2)}`)

    throw new Error(
      `${name} field from spreadsheet is not matching predefined ${name} values. Check console for list of values allowed (F12 on Keyboard).`,
    )
  }

  return fixedValue
}

export const dataSheetGridOnChangeExamResourcesValidation = (
  value: Record<string, any>[],
): string | undefined => {
  for (const row of value) {
    // Subject validation
    if (!erUploadSubjectsAllowed.includes(row.subject)) {
      console.error(
        `Subject field from spreadsheet is not matching predefined Subject values. This field is case sensitive, please use one of values allowed: `,
        JSON.stringify(erUploadSubjectsAllowed),
        { value: row.subject },
      )

      return `Subject: ${row.subject} not recognized by the system!`
    }

    // Exam Board validation
    if (!erUploadBoardsAllowed.includes(row.examBoard)) {
      console.error(
        `Exam Board field from spreadsheet is not matching predefined Exam Board values. This field is case sensitive, please use one of values allowed: `,
        JSON.stringify(erUploadBoardsAllowed),
        { value: row.examBoard },
      )

      return `Exam Board: ${row.examBoard} not recognized by the system!`
    }

    // Exam Level validation
    if (!erUploadLevelsAllowed.includes(row.examLevel)) {
      console.error(
        `Exam Level field from spreadsheet is not matching predefined Exam Level values. This field is case sensitive, please use one of values allowed: `,
        JSON.stringify(erUploadLevelsAllowed),
        { value: row.examLevel },
      )
      return `Exam Level: ${row.examLevel} not recognized by the system!`
    }

    // Difficulty validation
    if (!erUploadDifficultiesAllowed.includes(row.difficulty)) {
      console.error(
        `Difficulty field from spreadsheet is not matching predefined Difficulty values. This field is case sensitive, please use one of values allowed: `,
        JSON.stringify(erUploadDifficultiesAllowed),
        { value: row.difficulty },
      )
      return `Difficulty: ${row.difficulty} not recognized by the system!`
    }

    // Resource Type validation
    if (!erUploadResourceTypesAllowed.includes(row.resourceType)) {
      console.error(
        `Resource Type field from spreadsheet is not matching predefined Resource Type values. This field is case sensitive, please use one of values allowed: `,
        JSON.stringify(erUploadResourceTypesAllowed),
        { value: row.resourceType },
      )
      return `Resource Type: ${row.resourceType} not recognized by the system!`
    }

    // calculator should be either "yes" or "no"
    if (
      !!row.calculator &&
      row.calculator !== 'yes' &&
      row.calculator !== 'no'
    ) {
      console.error(
        `Calculator field from spreadsheet is not matching predefined Calculator values. This field is case sensitive, please use one of values allowed: `,
        JSON.stringify(['yes', 'no']),
        { value: row.calculator },
      )
      return "Calculator must be either 'yes' or 'no'!"
    }

    if (!row.topic) {
      console.error(`Topic field is required for row: ${row.title}`, { row })
      return 'Topic is required!'
    }

    if (!row.questionNo) {
      console.error(`Question No field is required for row: ${row.title}`, {
        row,
      })
      return 'Question No is required!'
    }

    if (!row.title) {
      console.error(`Title field is required for row: ${row.title}`, {
        row,
      })
      return 'Title is required!'
    }
  }

  return undefined
}

const emptyValueAllowedKeys = ['date']

export function uploadExamResourceBeforeSaveValidation(
  resources: ICreateExamResource[],
): boolean {
  return resources.every((resource) => {
    // check top-level fields of ICreateExamPaper
    for (const key in resource) {
      // check for allowed empty value field
      if (emptyValueAllowedKeys.includes(key)) {
        continue
      }

      const value = resource[key as keyof ICreateExamResource]

      if (value === null || value === undefined) {
        const errorMessage = `Missing value for key: ${key} in resource: ${resource.title}`
        console.error(errorMessage)

        throw new Error(errorMessage)
      }

      // check key 'id' is empty or an empty string
      if (
        key === 'id' &&
        (value === '' || value === null || value === undefined)
      ) {
        const errorMessage = `Invalid or empty 'id' value in resource: ${resource.title}`
        console.error(errorMessage)

        throw new Error(errorMessage)
      }

      if (key === 'subject') {
        if (!erUploadSubjectsAllowed.includes(value as CourseSubject)) {
          const errorMessage = `Invalid value for field '${key}' in resource: ${resource.title}, ${resource.topic}`
          console.error(errorMessage, JSON.stringify(erUploadSubjectsAllowed), {
            value,
          })

          throw new Error(errorMessage)
        }
      }
      if (key === 'examBoard') {
        if (!erUploadBoardsAllowed.includes(value as ExamBoards)) {
          const errorMessage = `Invalid value for field '${key}' in resource: ${resource.title}, ${resource.topic}`
          console.error(errorMessage, JSON.stringify(erUploadBoardsAllowed), {
            value,
          })

          throw new Error(errorMessage)
        }
      }
      if (key === 'examLevel') {
        if (!erUploadLevelsAllowed.includes(value as ExamLevel)) {
          const errorMessage = `Invalid value for field '${key}' in resource: ${resource.title}, ${resource.topic}`
          console.error(errorMessage, JSON.stringify(erUploadLevelsAllowed), {
            value,
          })

          throw new Error(errorMessage)
        }
      }
      if (key === 'difficulty') {
        if (
          !erUploadDifficultiesAllowed.includes(
            value as ExamResourcesDifficulty,
          )
        ) {
          const errorMessage = `Invalid value for field '${key}' in resource: ${resource.title}, ${resource.topic}`
          console.error(
            errorMessage,
            JSON.stringify(erUploadDifficultiesAllowed),
            {
              value,
            },
          )

          throw new Error(errorMessage)
        }
      }
      if (key === 'resourcesType') {
        if (!erUploadResourceTypesAllowed.includes(value as ResourceType)) {
          const errorMessage = `Invalid value for field '${key}' in resource: ${resource.title}, ${resource.topic}`
          console.error(
            errorMessage,
            JSON.stringify(erUploadResourceTypesAllowed),
            {
              value,
            },
          )

          throw new Error(errorMessage)
        }
      }
    }

    return true
  })
}
